import { useMemo } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LightboxProvider } from './components/Lightbox';
import { HomeDataProvider, useHomeData } from './contexts/HomeDataContext';
import { ConfirmationPage } from './pages/ConfirmationPage';
import { HomePage } from './pages/HomePage';
import { ExperienceDetailPage } from './pages/ExperienceDetailPage';
import { PaymentRedirectPage } from './pages/PaymentRedirectPage';
import { BookingFlow } from './flow/BookingFlow';

type Route =
  | { kind: 'home' }
  | { kind: 'confirmation' }
  | { kind: 'return' }
  | { kind: 'completion' }
  | { kind: 'booking'; code: string }
  | { kind: 'detail'; code: string };

function readRoute(): Route {
  const path = window.location.pathname.replace(/\/$/, '');
  if (path === '' || path === '/index.html') return { kind: 'home' };
  if (path === '/confirmation') return { kind: 'confirmation' };
  // Rotas de redirect pos-pagamento (AbacatePay):
  //   /return     — pagamento nao finalizado
  //   /completion — pagamento recebido
  if (path === '/return') return { kind: 'return' };
  if (path === '/completion') return { kind: 'completion' };

  const bookingMatch = path.match(/^\/booking\/(.+)$/);
  if (bookingMatch) return { kind: 'booking', code: decodeURIComponent(bookingMatch[1]) };

  // Fallback: any other single-segment path is treated as an experience code/slug.
  // The query param ?code=... still works as an alias.
  const qp = new URL(window.location.href).searchParams.get('code');
  if (qp) return { kind: 'detail', code: qp };

  const parts = path.split('/').filter(Boolean);
  return { kind: 'detail', code: parts[parts.length - 1] };
}

export default function App() {
  const route = useMemo(readRoute, []);
  const onHome = route.kind === 'home';

  // Paginas minimalistas (sem header/footer) — chegam por redirect externo
  // (gateway de pagamento). Fluxo principal volta pelo WhatsApp.
  const isPaymentRedirect = route.kind === 'return' || route.kind === 'completion';

  if (isPaymentRedirect) {
    return (
      <LightboxProvider>
        <HomeDataProvider>
          {route.kind === 'return' ? (
            <PaymentRedirectPage
              variant="pending"
              title="Pagamento não finalizado"
              message="Você pode voltar ao WhatsApp para continuar o agendamento."
            />
          ) : (
            <PaymentRedirectPage
              variant="success"
              title="Pagamento recebido!"
              message="Estamos confirmando sua reserva. Você receberá uma mensagem no WhatsApp em instantes."
            />
          )}
        </HomeDataProvider>
      </LightboxProvider>
    );
  }

  return (
    <LightboxProvider>
      <HomeDataProvider>
        <div className="site">
          <Header />
          <div className="site-content">
            {route.kind === 'confirmation' ? (
              <ConfirmationPage />
            ) : route.kind === 'home' ? (
              <HomePage />
            ) : route.kind === 'booking' ? (
              <BookingRoute code={route.code} />
            ) : (
              <DetailRoute code={route.code} />
            )}
          </div>
          {onHome || route.kind === 'confirmation' ? <Footer /> : null}
        </div>
      </HomeDataProvider>
    </LightboxProvider>
  );
}

function findListItem(code: string, list: ReturnType<typeof useHomeData>['allExperiences']) {
  if (!list) return null;
  const norm = (s: string) => s.toLowerCase().trim();
  const target = norm(code);
  return list.find((e) => norm(e.experienceCode) === target || norm(e.slug) === target) ?? null;
}

function DetailRoute({ code }: { code: string }) {
  const { allExperiences } = useHomeData();
  const exp = useMemo(() => findListItem(code, allExperiences), [code, allExperiences]);

  if (allExperiences === null) {
    return (
      <main className="app">
        <div className="skeleton skeleton--cover" />
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--line skeleton--line-sm" />
      </main>
    );
  }

  if (!exp) {
    return (
      <main className="app">
        <div className="error-box">
          <h3>Experiência não encontrada</h3>
          <p>O link pode estar quebrado ou a experiência foi removida.</p>
          <a className="btn btn--primary" href="/">Voltar para a home</a>
        </div>
      </main>
    );
  }

  return <ExperienceDetailPage exp={exp} />;
}

function BookingRoute({ code }: { code: string }) {
  const { allExperiences } = useHomeData();
  const exp = useMemo(() => findListItem(code, allExperiences), [code, allExperiences]);

  if (allExperiences === null) {
    return (
      <main className="app">
        <div className="skeleton skeleton--cover" />
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--line skeleton--line-sm" />
      </main>
    );
  }

  if (!exp) {
    return (
      <main className="app">
        <div className="error-box">
          <h3>Experiência não encontrada</h3>
          <p>Não foi possível iniciar a reserva.</p>
          <a className="btn btn--primary" href="/">Voltar para a home</a>
        </div>
      </main>
    );
  }

  return <BookingFlow exp={exp} />;
}
